<template>
  <div class="study-page" v-loading="loading">
    <div class="study-shell">
      <section v-if="showStudySwitcher" class="study-switcher">
        <div class="major-tabs-wrapper" v-if="showMajorTabsRow">
          <div
            v-for="group in majorSwitchGroups"
            :key="`major-${group.majorType}`"
            class="major-tab"
            :class="{
              'is-active': Number(selectedMajorType) === Number(group.majorType)
            }"
            @click="handleMajorSwitch(group.majorType)"
          >
            {{ group.label }}
          </div>
        </div>

        <div class="exam-tabs-wrapper" v-if="showExamTabsRow">
          <div
            v-for="exam in examsInSelectedMajor"
            :key="`exam-${exam.erId}`"
            class="exam-tab"
            :class="{
              'is-active': Number(selectedErId) === Number(exam.erId)
            }"
            :title="exam.name"
            @click="handleExamSwitch(exam.erId)"
          >
            {{ exam.name }}
          </div>
        </div>
      </section>

      <section class="hero-panel">
        <div
          class="hero-banner"
          role="img"
          aria-label="我的培训背景图"
          :style="{ backgroundImage: `url(${studyBackground})` }"
        >
          <div class="hero-banner-content">
            <template v-if="currentExam">
              <!-- Top: Header (Status Tag + Detail Button) -->
              <div class="hero-header">
                <div class="hero-status-tag" :class="heroStatus.className">
                  {{ heroStatus.statusText }}
                </div>
                <button
                  type="button"
                  class="hero-detail-btn"
                  @click="goToCurrentExamDetail"
                >
                  查看详情 <i class="el-icon-arrow-right"></i>
                </button>
              </div>

              <!-- Middle: Main Info (Title + Date) -->
              <div class="hero-info">
                <h1 class="hero-exam-name" :title="currentExam.name">
                  {{ currentExam.name || "已报名培训" }}
                </h1>
                <div
                  v-if="shouldShowExamFailingHint"
                  class="hero-extra-fail-tip"
                >
                  很遗憾，您未通过考试
                </div>
                <div class="hero-date">
                  <i class="el-icon-time" style="margin-right:4px"></i>
                  {{ heroStatus.dateText }}
                </div>
              </div>

              <!-- Bottom: Footer (Progress + Action) -->
              <div class="hero-footer">
                <div class="hero-progress-container">
                  <div class="hero-progress-text">
                    <span class="label">学习进度</span>
                    <span class="value">{{ heroLearnStats.percent }}%</span>
                    <span class="sub-text"
                      >({{ heroLearnStats.doneText }}/{{
                        heroLearnStats.needText
                      }}
                      学时)</span
                    >
                  </div>
                  <div class="hero-progress-bar">
                    <span
                      class="hero-progress-inner"
                      :style="{ width: `${heroLearnStats.percent}%` }"
                    ></span>
                  </div>
                </div>

                <div class="hero-action-Wrapper">
                  <button
                    v-if="heroStatus.showAction"
                    type="button"
                    class="hero-main-btn"
                    :class="{ 'is-disabled': heroStatus.actionDisabled }"
                    :disabled="heroStatus.actionDisabled"
                    @click="handleHeroAction"
                  >
                    {{ heroStatus.actionText }}
                  </button>
                </div>
              </div>
            </template>

            <div class="hero-empty" v-else>
              <div class="hero-empty-icon">
                <i class="el-icon-files"></i>
              </div>
              <div class="hero-empty-text">
                <div class="hero-empty-title">暂无进行中的培训</div>
                <div class="hero-empty-desc">
                  报名成功后，此处将显示您的学习进度与考试状态
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="hero-actions">
          <div class="function-grid">
            <button
              v-for="item in functionList"
              :key="item.name"
              type="button"
              class="function-item"
              @click="handleFunctionClick(item)"
            >
              <span class="function-icon" :style="{ background: item.bgColor }">
                <i :class="item.icon"></i>
              </span>
              <span class="function-name">{{ item.name }}</span>
            </button>
          </div>
        </div>
      </section>

      <section v-if="showTheorySurveyEntry" class="survey-entry-card">
        <div class="survey-entry-left">
          <img
            class="survey-entry-icon"
            :src="surveyIcon"
            alt="理论培训满意度调查表"
          />
          <span class="survey-entry-title">理论培训满意度调查表</span>
        </div>
        <button
          type="button"
          class="survey-entry-btn"
          @click="goToTheorySurvey"
        >
          立即填写
        </button>
      </section>

      <section v-if="showBaseFeedbackEntry" class="survey-entry-card">
        <div class="survey-entry-left">
          <img
            class="survey-entry-icon"
            :src="surveyIcon"
            alt="单位教学质量反馈表"
          />
          <span class="survey-entry-title">单位教学质量反馈表</span>
        </div>
        <button
          type="button"
          class="survey-entry-btn"
          @click="goToBaseFeedback"
        >
          立即填写
        </button>
      </section>

      <section v-if="showPaperUploadEntry" class="paper-upload-entry-card">
        <div class="paper-upload-entry-left">
          <span class="paper-upload-entry-icon">
            <i class="el-icon-upload2"></i>
          </span>
          <span
            class="paper-upload-entry-title"
            :class="paperUploadState.statusClass"
            >{{ paperUploadState.text }}</span
          >
        </div>
        <button
          v-if="paperUploadState.showButton"
          type="button"
          class="paper-upload-entry-btn"
          :class="{ 'is-disabled': paperUploadState.buttonDisabled }"
          :disabled="paperUploadState.buttonDisabled || uploadingPaper"
          @click="openPaperUploadDialog"
        >
          {{ paperUploadState.buttonText }}
        </button>
      </section>

      <section class="study-hours-card">
        <span class="study-hours-label">您已累计学习</span>
        <span class="study-hours-value">
          <span class="study-hours-num">{{ studyHoursDisplay }}</span>
          <span class="study-hours-unit">学时</span>
        </span>
      </section>

      <section class="exam-lib-section">
        <div class="exam-lib-header">试题集</div>
        <div class="exam-lib-body">
          <el-empty
            v-if="questionSets.length === 0"
            description="暂无试题集"
            :image-size="90"
          />
          <div v-else class="exam-lib-grid">
            <article
              v-for="item in questionSets"
              :key="getLibId(item)"
              class="exam-lib-card"
              :class="{ 'is-locked': isLibLocked(item) }"
              @click="goToQuestionSet(item)"
            >
              <div class="exam-lib-cover">
                <img
                  class="cover-image"
                  :src="getCoverUrl(item)"
                  :alt="getExamLibName(item)"
                />
              </div>
              <div class="exam-lib-footer">
                <div class="footer-texts">
                  <div class="footer-top-line">
                    <span class="answered-text"
                      >已答题：{{ getAnsweredQuestions(item) }}/{{
                        getTotalQuestions(item)
                      }}</span
                    >
                    <i
                      v-if="isLibLocked(item)"
                      class="el-icon-lock lock-icon"
                    ></i>
                  </div>
                  <span v-if="shouldShowEnrollTip(item)" class="enroll-tip">{{
                    getEnrollTipText(item)
                  }}</span>
                  <span v-else class="enroll-tip enroll-tip-placeholder"
                    >&nbsp;</span
                  >
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <el-dialog
        custom-class="practice-dialog"
        title=""
        :visible.sync="practiceDialogVisible"
      >
        <div class="lib-name">{{ getExamLibName(currentExamLib) }}</div>
        <div class="lib-question-count">
          共{{ getTotalQuestions(currentExamLib) }}道题
        </div>
        <div class="lib-echarts">
          <div class="lib-answered-echart">
            <el-progress
              type="circle"
              :stroke-width="20"
              color="#00B0AB"
              :percentage="getAnsweredPercent(currentExamLib)"
            ></el-progress>
            <div class="lib-echart-title">已答题</div>
          </div>
          <div class="lib-accuracy-echart">
            <el-progress
              type="circle"
              :stroke-width="20"
              color="#00B0AB"
              :percentage="getAccuracyPercent(currentExamLib)"
            ></el-progress>
            <div class="lib-echart-title">正确率</div>
          </div>
        </div>
        <div class="practice-type-list">
          <div
            class="practice-type"
            :class="{ 'selected-practice-type': practiceType === 0 }"
            @click="practiceType = 0"
          >
            全部
          </div>
          <div
            class="practice-type"
            :class="{ 'selected-practice-type': practiceType === 1 }"
            @click="practiceType = 1"
          >
            未做的题
          </div>
          <div
            class="practice-type"
            :class="{ 'selected-practice-type': practiceType === 2 }"
            @click="practiceType = 2"
          >
            错题
          </div>
          <div
            class="practice-type"
            :class="{ 'selected-practice-type': practiceType === 3 }"
            @click="practiceType = 3"
          >
            已答的题
          </div>
        </div>
        <div class="practice-btn" @click="go2PracticeExam">开始答题</div>
      </el-dialog>

      <el-dialog
        custom-class="paper-upload-dialog"
        title="上传论文"
        :visible.sync="paperUploadDialogVisible"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
      >
        <div class="paper-upload-dialog-body">
          <el-upload
            class="paper-file-uploader"
            action="/file/importOffice"
            name="officeFile"
            :show-file-list="false"
            :http-request="handlePaperUploadFile"
            :disabled="uploadingPaper"
            accept=".doc,.docx,.pdf"
          >
            <button
              type="button"
              class="paper-select-btn"
              :disabled="uploadingPaper"
            >
              {{ uploadingPaper ? "上传中..." : "选择文件" }}
            </button>
          </el-upload>
          <div v-if="paperUploadFileName" class="paper-selected-file">
            {{ paperUploadFileName }}
          </div>
          <button
            type="button"
            class="paper-close-btn"
            @click="paperUploadDialogVisible = false"
          >
            关闭
          </button>
        </div>
      </el-dialog>

      <el-dialog
        custom-class="cert-preview-dialog"
        :show-close="false"
        :visible.sync="showCertOverlay"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
      >
        <template slot="title">
          <div class="cert-dialog-header">
            <span class="cert-dialog-title">查看证书</span>
            <div class="cert-dialog-actions">
              <el-button type="primary" size="small" icon="el-icon-download" @click="downloadCertificate" :loading="downloadingCert">下载证书</el-button>
              <i class="el-icon-close close-icon" @click="showCertOverlay = false"></i>
            </div>
          </div>
        </template>
        <div class="cert-preview-body" v-loading="certificateLoading">
          <img
            v-if="certPreviewUrl"
            class="cert-preview-img"
            :src="certPreviewUrl"
            alt="证书图片"
          />
        </div>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { httpRequest } from "@/axios/axios-api";

const DEFAULT_MAJOR_TYPE = 0;
const SUBMITTED_THEORY_SURVEY_CACHE_KEY = "pc_submitted_theory_survey_ar_ids";
const SUBMITTED_BASE_FEEDBACK_CACHE_KEY = "pc_submitted_base_feedback_ar_ids";
const MAJOR_TYPE_LABELS = {
  0: "专科",
  1: "专项",
  2: "专题",
  3: "师资"
};

export default {
  name: "StudyPage",
  data() {
    return {
      loading: false,
      studyBackground: require("@/assets/home/studyBackGround.png"),
      defaultCover: require("@/assets/home/no_exam_bg.png"),
      surveyIcon: require("@/assets/home/live.png"),
      userInfo: {},
      studyHours: 0,
      yetEnrollExams: [],
      selectedMajorType: null,
      selectedErId: null,
      questionSets: [],
      paperUploadDialogVisible: false,
      paperUploadFileName: "",
      uploadingPaper: false,
      showCertOverlay: false,
      certificateLoading: false,
      certPreviewUrl: "",
      practiceDialogVisible: false,
      currentExamLib: null,
      practiceType: 0,
      functionList: [
        {
          name: "教学大纲",
          icon: "el-icon-document",
          bgColor: "#2184c6",
          path: "/courseList",
          query: {
            majorType: 0,
            category: 2
          }
        },
        {
          name: "教材",
          icon: "el-icon-reading",
          bgColor: "#2ab48f",
          path: "/courseList",
          query: {
            majorType: 0,
            category: 1
          }
        },
        {
          name: "专家讲义",
          icon: "el-icon-user",
          bgColor: "#f4a552",
          path: "/courseList",
          query: {
            majorType: 0,
            category: 0
          }
        },
        {
          name: "视频课程",
          icon: "el-icon-video-play",
          bgColor: "#558be7",
          path: "/videoCourseList"
        },
        {
          name: "直播课程",
          icon: "el-icon-video-camera",
          bgColor: "#e94992",
          path: "/liveCourseList"
        },
        {
          name: "练习题库",
          icon: "el-icon-collection",
          bgColor: "#9f74df",
          path: "/practice"
        },
        {
          name: "模拟试题",
          icon: "el-icon-finished",
          bgColor: "#26a69a",
          path: "/mockExamLib"
        },
        {
          name: "考试",
          icon: "el-icon-edit-outline",
          bgColor: "#3c8cff",
          path: "/formalExam"
        }
      ],
      downloadingCert: false
    };
  },
  computed: {
    majorType() {
      const selectedExamMajorType = Number(
        this.currentExam && this.currentExam.majorType
      );
      if (Number.isFinite(selectedExamMajorType)) {
        return selectedExamMajorType;
      }
      const raw = Number(this.userInfo.majorType);
      return Number.isFinite(raw) ? raw : DEFAULT_MAJOR_TYPE;
    },
    majorSwitchGroups() {
      if (
        !Array.isArray(this.yetEnrollExams) ||
        this.yetEnrollExams.length < 1
      ) {
        return [];
      }
      const majorMap = new Map();
      this.yetEnrollExams.forEach(exam => {
        const majorType = Number(exam && exam.majorType);
        if (!Number.isFinite(majorType)) {
          return;
        }
        if (!majorMap.has(majorType)) {
          majorMap.set(majorType, []);
        }
        majorMap.get(majorType).push(exam);
      });
      const presentTypes = Array.from(majorMap.keys()).sort((a, b) => a - b);
      return presentTypes.map(majorType => ({
        majorType,
        label: this.getMajorTypeLabel(majorType),
        exams: majorMap.get(majorType) || []
      }));
    },
    showStudySwitcher() {
      return (
        Array.isArray(this.yetEnrollExams) && this.yetEnrollExams.length > 1
      );
    },
    showMajorTabsRow() {
      return this.showStudySwitcher && this.majorSwitchGroups.length > 1;
    },
    selectedMajorGroup() {
      if (!this.showStudySwitcher || this.majorSwitchGroups.length < 1) {
        return null;
      }
      const selectedMajorType = Number(this.selectedMajorType);
      return (
        this.majorSwitchGroups.find(
          group => Number(group.majorType) === selectedMajorType
        ) || this.majorSwitchGroups[0]
      );
    },
    examsInSelectedMajor() {
      if (
        !this.selectedMajorGroup ||
        !Array.isArray(this.selectedMajorGroup.exams)
      ) {
        return [];
      }
      return this.selectedMajorGroup.exams;
    },
    showExamTabsRow() {
      return this.showStudySwitcher && this.examsInSelectedMajor.length > 1;
    },
    studyHoursDisplay() {
      const hours = Number(this.studyHours);
      return Number.isFinite(hours) ? hours.toFixed(2) : "0.00";
    },
    currentExam() {
      if (
        !Array.isArray(this.yetEnrollExams) ||
        this.yetEnrollExams.length === 0
      ) {
        return null;
      }
      const selectedErId = Number(this.selectedErId);
      if (Number.isFinite(selectedErId)) {
        const matchedExam = this.yetEnrollExams.find(
          exam => Number(exam && exam.erId) === selectedErId
        );
        if (matchedExam) {
          return matchedExam;
        }
      }
      return this.yetEnrollExams[0] || null;
    },
    heroLearnStats() {
      if (!this.currentExam || !this.currentExam.apply) {
        return {
          doneHours: 0,
          needHours: 0,
          doneText: "0",
          needText: "0",
          percent: 0
        };
      }
      const doneHours = this.convertLearnDurationToHour(
        this.currentExam.apply.yetLearnDuration
      );
      const needHours = this.convertLearnDurationToHour(
        this.currentExam.apply.needLearnDuration
      );
      const percent =
        needHours > 0
          ? Math.min(
              100,
              Math.max(0, Math.round((doneHours / needHours) * 100))
            )
          : 0;
      return {
        doneHours,
        needHours,
        doneText: this.formatFloatDisplay(doneHours),
        needText: this.formatFloatDisplay(needHours),
        percent
      };
    },
    heroStatus() {
      return this.buildHeroStatus(this.currentExam);
    },
    shouldShowExamFailingHint() {
      const apply = this.currentExam && this.currentExam.apply;
      if (!apply) {
        return false;
      }
      const examCount = Number(apply.examCount);
      const isPass = Number(apply.isPass);
      return Number.isFinite(examCount) && examCount >= 2 && isPass !== 2;
    },
    showPaperUploadEntry() {
      if (!this.currentExam || !this.currentExam.apply) {
        return false;
      }
      const apply = this.currentExam.apply;
      const isPass = Number(apply.isPass) === 2;
      return this.isFirstExamPaperRequired(this.currentExam) && isPass;
    },
    paperUploadState() {
      if (!this.showPaperUploadEntry) {
        return {
          text: "",
          buttonText: "",
          buttonDisabled: true,
          showButton: false,
          statusClass: ""
        };
      }
      const exam = this.currentExam;
      const deadlineText = this.getPaperUploadDeadlineText(exam);
      const apply = (exam && exam.apply) || {};
      const paperUrl =
        typeof apply.paperUrl === "string" ? apply.paperUrl.trim() : "";
      const isCheckPass = Number(apply.isCheckPass);
      if (!paperUrl) {
        return {
          text: `论文待上传(截止时间：${deadlineText})`,
          buttonText: "上传",
          buttonDisabled: false,
          showButton: true,
          statusClass: "is-pending"
        };
      }
      if (isCheckPass === 1) {
        return {
          text: `论文已被驳回(截止时间：${deadlineText})`,
          buttonText: "重新上传",
          buttonDisabled: false,
          showButton: true,
          statusClass: "is-rejected"
        };
      }
      if (isCheckPass === 2) {
        return {
          text: `论文审核通过(截止时间：${deadlineText})`,
          buttonText: "",
          buttonDisabled: true,
          showButton: false,
          statusClass: "is-passed"
        };
      }
      return {
        text: `论文审核中(截止时间：${deadlineText})`,
        buttonText: "审核中",
        buttonDisabled: true,
        showButton: true,
        statusClass: "is-reviewing"
      };
    },
    showTheorySurveyEntry() {
      const exam = this.currentExam;
      if (!exam || Number(exam.majorType) === 3 || !exam.apply) {
        return false;
      }
      const arId = Number(exam.apply.arId);
      if (!Number.isFinite(arId) || arId <= 0) {
        return false;
      }
      const rrssrId = Number(exam.apply.rrssrId);
      if (
        (Number.isFinite(rrssrId) && rrssrId > 0) ||
        this.isSurveySubmittedLocal(arId)
      ) {
        return false;
      }
      const isPass = Number(exam.apply.isPass);
      const examCount = Number(exam.apply.examCount);
      return isPass === 2 || (Number.isFinite(examCount) && examCount >= 2);
    },
    showBaseFeedbackEntry() {
      const exam = this.currentExam;
      if (!exam || !exam.apply) {
        return false;
      }
      const arId = Number(exam.apply.arId);
      if (!Number.isFinite(arId) || arId <= 0) {
        return false;
      }
      const btqfrId = Number(exam.apply.btqfrId);
      if (
        (Number.isFinite(btqfrId) && btqfrId > 0) ||
        this.isBaseFeedbackSubmittedLocal(arId)
      ) {
        return false;
      }
      const isPracticePass = Number(exam.apply.isPracticePass);
      return isPracticePass === 1 || isPracticePass === 2;
    }
  },
  created() {
    this.initPage();
  },
  methods: {
    async initPage() {
      this.loading = true;
      try {
        await this.loadUserInfo();
        await this.loadExamLibData();
      } finally {
        this.loading = false;
      }
    },
    async loadUserInfo() {
      try {
        const res = await httpRequest("/app/user", "get", null);
        if (res && res.code === 200 && res.data) {
          this.userInfo = res.data;
        } else {
          this.userInfo = {};
        }
      } catch (error) {
        void error;
        this.userInfo = {};
      }
    },
    getMajorTypeLabel(majorType) {
      const key = Number(majorType);
      return MAJOR_TYPE_LABELS[key] || "培训";
    },
    ensureExamSelection() {
      if (
        !Array.isArray(this.yetEnrollExams) ||
        this.yetEnrollExams.length < 1
      ) {
        this.selectedMajorType = null;
        this.selectedErId = null;
        return;
      }
      const groups = this.majorSwitchGroups;
      if (groups.length < 1) {
        this.selectedMajorType = null;
        this.selectedErId = null;
        return;
      }
      const selectedMajorType = Number(this.selectedMajorType);
      const majorExists = groups.some(
        group => Number(group.majorType) === selectedMajorType
      );
      const finalMajorType = majorExists
        ? selectedMajorType
        : Number(groups[0].majorType);
      this.selectedMajorType = finalMajorType;

      const selectedMajorGroup =
        groups.find(group => Number(group.majorType) === finalMajorType) ||
        groups[0];
      const exams = (selectedMajorGroup && selectedMajorGroup.exams) || [];
      if (exams.length < 1) {
        this.selectedErId = null;
        return;
      }

      const selectedErId = Number(this.selectedErId);
      const examExists = exams.some(
        exam => Number(exam && exam.erId) === selectedErId
      );
      if (examExists) {
        this.selectedErId = selectedErId;
        return;
      }
      const firstErId = Number(exams[0] && exams[0].erId);
      this.selectedErId = Number.isFinite(firstErId) ? firstErId : null;
    },
    async loadCurrentExamRelatedData() {
      const exam = this.currentExam;
      this.studyHours = this.resolveStudyHoursFromExam(exam);
      if (!exam) {
        this.questionSets = [];
        return;
      }
      const currentMajorType = Number(exam.majorType);
      const majorType = Number.isFinite(currentMajorType)
        ? currentMajorType
        : this.majorType;
      const majorId = this.resolveMajorIdForAnswer([exam]);
      try {
        const totalRes = await httpRequest("/app/answer/total", "get", {
          majorType,
          majorId
        });
        if (totalRes && totalRes.code === 200 && totalRes.data) {
          const total = totalRes.data;
          this.questionSets = Array.isArray(total.examLibs)
            ? total.examLibs
            : [];
          return;
        }
      } catch (error) {
        void error;
      }
      this.questionSets = [];
    },
    async loadExamLibData() {
      this.yetEnrollExams = await this.requestYetEnrollExams(null);
      this.ensureExamSelection();
      await this.loadCurrentExamRelatedData();
    },
    async handleMajorSwitch(majorType) {
      const nextMajorType = Number(majorType);
      if (!Number.isFinite(nextMajorType)) {
        return;
      }
      if (Number(this.selectedMajorType) === nextMajorType) {
        return;
      }
      this.selectedMajorType = nextMajorType;
      const majorGroup = this.majorSwitchGroups.find(
        group => Number(group.majorType) === nextMajorType
      );
      const exams = (majorGroup && majorGroup.exams) || [];
      const firstErId = Number(exams[0] && exams[0].erId);
      this.selectedErId = Number.isFinite(firstErId) ? firstErId : null;
      this.loading = true;
      try {
        await this.loadCurrentExamRelatedData();
      } finally {
        this.loading = false;
      }
    },
    async handleExamSwitch(erId) {
      const nextErId = Number(erId);
      if (!Number.isFinite(nextErId)) {
        return;
      }
      if (Number(this.selectedErId) === nextErId) {
        return;
      }
      this.selectedErId = nextErId;
      this.loading = true;
      try {
        await this.loadCurrentExamRelatedData();
      } finally {
        this.loading = false;
      }
    },
    resolveStudyHoursFromExam(exam) {
      if (!exam || !exam.apply) {
        return 0;
      }
      return this.convertLearnDurationToHour(exam.apply.yetLearnDuration);
    },
    async requestYetEnrollExams(params) {
      try {
        const enrollRes = await httpRequest(
          "/app/exam/exam/yet-enroll-exams",
          "get",
          params
        );
        if (
          enrollRes &&
          enrollRes.code === 200 &&
          Array.isArray(enrollRes.data)
        ) {
          return enrollRes.data;
        }
      } catch (error) {
        void error;
      }
      return [];
    },
    resolveMajorIdForAnswer(examList) {
      if (!Array.isArray(examList) || examList.length === 0) {
        return "";
      }
      const firstExam = examList[0] || {};
      if (
        firstExam.majorId === null ||
        firstExam.majorId === undefined ||
        firstExam.majorId === ""
      ) {
        return "";
      }
      return firstExam.majorId;
    },
    convertLearnDurationToHour(duration) {
      const seconds = Number(duration);
      if (!Number.isFinite(seconds) || seconds <= 0) {
        return 0;
      }
      return Number((seconds / 60 / 45).toFixed(2));
    },
    formatFloatDisplay(value) {
      const num = Number(value);
      if (!Number.isFinite(num)) {
        return "0";
      }
      if (Number.isInteger(num)) {
        return String(num);
      }
      return num
        .toFixed(2)
        .replace(/0+$/, "")
        .replace(/\.$/, "");
    },
    calcRemainLearnHours(apply) {
      if (!apply) {
        return "0.00";
      }
      const needDuration = Number(apply.needLearnDuration);
      const doneDuration = Number(apply.yetLearnDuration);
      if (!Number.isFinite(needDuration) || needDuration <= 0) {
        return "0.00";
      }
      const remainDuration = Math.max(
        0,
        needDuration - (Number.isFinite(doneDuration) ? doneDuration : 0)
      );
      return (remainDuration / 60 / 45).toFixed(2);
    },
    formatDateDisplay(dateTime) {
      if (!dateTime) {
        return "-";
      }
      const text = String(dateTime)
        .replace("T", " ")
        .trim();
      if (!text) {
        return "-";
      }
      if (text.length >= 19) {
        return text.slice(0, 19).replace(/-/g, "/");
      }
      return text.replace(/-/g, "/");
    },
    formatDateRangeDisplay(startTime, endTime) {
      if (!startTime && !endTime) {
        return "-";
      }
      return `${this.formatDateDisplay(startTime)}~${this.formatDateDisplay(
        endTime
      )}`;
    },
    isAfterNow(dateText) {
      const date = this.parseDateTime(dateText);
      if (!date) {
        return false;
      }
      return Date.now() >= date.getTime();
    },
    buildHeroState(statusText, dateText, options = {}) {
      return {
        statusText: statusText || "学习进行中",
        dateText: dateText || "",
        className: options.className || "status-neutral",
        actionText: options.actionText || "",
        actionType: options.actionType || "",
        showAction: options.showAction === true,
        actionDisabled: options.actionDisabled === true
      };
    },
    buildHeroStatus(exam) {
      if (!exam) {
        return this.buildHeroState(
          "暂无已报名培训",
          "报名成功后，这里会展示学习进度和考试状态",
          {
            className: "status-muted",
            actionText: "去首页报名",
            actionType: "HOME",
            showAction: true
          }
        );
      }
      if (!exam.apply || !exam.flowStatus) {
        return this.buildHeroState("学习数据同步中", "请稍后刷新页面", {
          className: "status-muted"
        });
      }
      const majorType = Number(exam.majorType);
      const examType = Number(exam.apply.examType);
      const status = Number(exam.flowStatus.status);
      const paperStatus = Number(exam.flowStatus.paperStatus);
      const remainLearnHours = this.calcRemainLearnHours(exam.apply);

      if (majorType === 1 || majorType === 2) {
        return this.buildSpecialMajorStatus(status, exam, remainLearnHours);
      }
      if (majorType === 3) {
        return this.buildTeacherMajorStatus(status, exam, remainLearnHours);
      }
      if (majorType === 0 && examType === 1) {
        return this.buildRenewExamStatus(status, exam, remainLearnHours);
      }
      return this.buildFirstExamStatus(
        status,
        paperStatus,
        exam,
        remainLearnHours
      );
    },
    buildExamCoreStatus(status, exam, remainLearnHours) {
      const remainText = `还需${remainLearnHours}学时开放考试`;
      if (this.isExamAttemptsExhausted(exam)) {
        return this.buildHeroState("考试已结束", "考试机会已用完", {
          className: "status-error"
        });
      }
      switch (status) {
        case 0:
          return this.buildHeroState(
            remainText,
            `理论考试截止时间：${this.formatDateDisplay(exam.examEndTime)}`,
            { className: "status-warning" }
          );
        case 1:
          return this.buildHeroState(
            "您已满足考试条件",
            `理论考试截止时间：${this.formatDateDisplay(exam.examEndTime)}`,
            {
              className: "status-success",
              actionText: "立即考试",
              actionType: "EXAM",
              showAction: true
            }
          );
        case 2:
          return this.buildHeroState(
            "很遗憾，您未通过考试",
            `理论考试截止时间：${this.formatDateDisplay(exam.examEndTime)}`,
            {
              className: "status-warning",
              actionText: "重新考试",
              actionType: "EXAM",
              showAction: true
            }
          );
        case 3:
          return this.buildHeroState(
            "很遗憾，您未通过考试",
            "原因：理论考试未通过",
            {
              className: "status-error"
            }
          );
        case 13:
          return this.buildHeroState(
            "很遗憾，您未通过考试",
            "原因：考试时间已结束，理论考试未通过",
            { className: "status-error" }
          );
        case 15:
          return this.buildHeroState(
            "暂未到学习时间",
            `开始时间：${this.formatDateDisplay(exam.examStartTime)}`,
            { className: "status-muted" }
          );
        case 17:
          return this.buildHeroState(
            "恭喜，您已通过考试",
            `培训截止时间：${this.formatDateOnly(exam.trainEndTime)}`,
            {
              className: "status-success",
              actionText: "查看证书",
              actionType: "CERT",
              showAction: true
            }
          );
        default:
          return this.buildHeroState(
            "学习进行中",
            "请按培训流程完成当前学习任务",
            {
              className: "status-neutral"
            }
          );
      }
    },
    buildSpecialMajorStatus(status, exam, remainLearnHours) {
      if (status === 5) {
        const canPractice = this.isAfterNow(exam.practiceStartTime);
        return this.buildHeroState(
          "理论考试已通过",
          `实践考核时间：${this.formatDateRangeDisplay(
            exam.practiceStartTime,
            exam.practiceEndTime
          )}`,
          {
            className: "status-success",
            actionText: "实践考核",
            actionType: "PRACTICE",
            showAction: canPractice
          }
        );
      }
      if (status === 6) {
        return this.buildHeroState(
          "实践考核进行中",
          `实践考核截止时间：${this.formatDateDisplay(exam.practiceEndTime)}`,
          {
            className: "status-success",
            actionText: "实践考核",
            actionType: "PRACTICE",
            showAction: true
          }
        );
      }
      if (status === 7) {
        return this.buildHeroState(
          "很遗憾，您未通过考试",
          "原因：实践考核未通过",
          {
            className: "status-error"
          }
        );
      }
      return this.buildExamCoreStatus(status, exam, remainLearnHours);
    },
    buildTeacherMajorStatus(status, exam, remainLearnHours) {
      return this.buildExamCoreStatus(status, exam, remainLearnHours);
    },
    buildRenewExamStatus(status, exam, remainLearnHours) {
      return this.buildExamCoreStatus(status, exam, remainLearnHours);
    },
    buildFirstExamStatus(status, paperStatus, exam, remainLearnHours) {
      if (this.isPracticeAndPaperPassed(exam)) {
        return this.buildHeroState(
          "恭喜，您已通过考试",
          `培训截止时间：${this.formatDateOnly(exam.trainEndTime)}`,
          {
            className: "status-success",
            actionText: "查看证书",
            actionType: "CERT",
            showAction: true
          }
        );
      }
      if (status === 4) {
        return this.buildHeroState(
          "恭喜，您已通过考试",
          `选择实习单位截止时间：${this.formatDateDisplay(exam.choiceEndTime)}`,
          {
            className: "status-success",
            actionText: "选择实习单位",
            actionType: "CHOOSE_BASE",
            showAction: true
          }
        );
      }
      if (status === 44) {
        return this.buildHeroState(
          "恭喜，您已通过考试",
          `选择实习单位截止时间：${this.formatDateDisplay(exam.choiceEndTime)}`,
          {
            className: "status-success",
            actionText: "已选择实习单位",
            actionType: "NONE",
            showAction: true,
            actionDisabled: true
          }
        );
      }
      if (status === 5) {
        const canPractice = this.isAfterNow(exam.practiceStartTime);
        return this.buildHeroState(
          "实习单位已分配",
          `实践考核时间：${this.formatDateRangeDisplay(
            exam.practiceStartTime,
            exam.practiceEndTime
          )}`,
          {
            className: "status-success",
            actionText: "实践考核",
            actionType: "PRACTICE",
            showAction: canPractice
          }
        );
      }
      if (status === 6) {
        return this.buildHeroState(
          "实践考核进行中",
          `实践考核截止时间：${this.formatDateDisplay(exam.practiceEndTime)}`,
          {
            className: "status-success",
            actionText: "实践考核",
            actionType: "PRACTICE",
            showAction: true
          }
        );
      }
      if (status === 7) {
        return this.buildHeroState(
          "很遗憾，您未通过考试",
          "原因：实践考核未通过",
          {
            className: "status-error"
          }
        );
      }
      if (status === 8) {
        if (paperStatus === 16) {
          return this.buildHeroState(
            "上传论文",
            `培训截止时间：${this.formatDateDisplay(exam.trainEndTime)}`,
            {
              className: "status-success",
              actionText: "立即上传",
              actionType: "UPLOAD_PAPER",
              showAction: true
            }
          );
        }
        if (paperStatus === 9) {
          return this.buildHeroState(
            "论文正在审核中",
            `培训截止时间：${this.formatDateDisplay(exam.trainEndTime)}`,
            { className: "status-warning" }
          );
        }
        if (paperStatus === 10) {
          return this.buildHeroState(
            "很遗憾，您的论文被驳回",
            `培训截止时间：${this.formatDateDisplay(exam.trainEndTime)}`,
            {
              className: "status-error",
              actionText: "重新上传",
              actionType: "UPLOAD_PAPER",
              showAction: true
            }
          );
        }
        if (paperStatus === 11) {
          return this.buildHeroState(
            "恭喜您，您的论文已通过",
            `培训截止时间：${this.formatDateDisplay(exam.trainEndTime)}`,
            {
              className: "status-success",
              actionText: "查看证书",
              actionType: "CERT",
              showAction: true
            }
          );
        }
      }
      if (status === 14) {
        return this.buildHeroState(
          "很遗憾，您未通过考试",
          "原因：实习单位分配时间已过，实习单位未选择",
          { className: "status-error" }
        );
      }
      if (status === 19) {
        return this.buildHeroState(
          "很遗憾，您未通过考试",
          "原因：培训时间已到，论文审核未通过",
          { className: "status-error" }
        );
      }
      return this.buildExamCoreStatus(status, exam, remainLearnHours);
    },
    getCurrentErId() {
      const erId = Number(this.currentExam && this.currentExam.erId);
      if (!Number.isFinite(erId) || erId <= 0) {
        return null;
      }
      return erId;
    },
    getCurrentArId() {
      const arId = Number(
        this.currentExam &&
          this.currentExam.apply &&
          this.currentExam.apply.arId
      );
      if (!Number.isFinite(arId) || arId <= 0) {
        return null;
      }
      return arId;
    },
    getCurrentMajorId() {
      const majorId = Number(
        this.currentExam &&
          (this.currentExam.majorId ||
            (this.currentExam.apply && this.currentExam.apply.majorId))
      );
      if (!Number.isFinite(majorId) || majorId <= 0) {
        return null;
      }
      return majorId;
    },
    isFirstExamPaperRequired(exam) {
      if (!exam || !exam.apply) {
        return false;
      }
      const majorType = Number(exam.majorType);
      const examType = Number(exam.apply.examType);
      const isFirstExam = !Number.isFinite(examType) || examType === 0;
      return majorType === 0 && isFirstExam;
    },
    formatDateOnly(dateTime) {
      const date = this.parseDateTime(dateTime);
      if (!date) {
        return "-";
      }
      const year = date.getFullYear();
      const month = `${date.getMonth() + 1}`.padStart(2, "0");
      const day = `${date.getDate()}`.padStart(2, "0");
      return `${year}/${month}/${day}`;
    },
    getPaperUploadDeadlineText(exam) {
      if (!exam) {
        return "-";
      }
      return this.formatDateOnly(exam.trainEndTime);
    },
    isPracticeAndPaperPassed(exam) {
      const apply = exam && exam.apply;
      if (!apply) {
        return false;
      }
      return (
        Number(apply.isPracticePass) === 2 && Number(apply.isCheckPass) === 2
      );
    },
    normalizeCertificateUrl(url) {
      if (url === null || url === undefined) {
        return "";
      }
      const text = String(url).trim();
      return text || "";
    },
    resolveCurrentCertificateUrlFromExam() {
      const exam = this.currentExam || {};
      const apply = exam.apply || {};
      const certificate = apply.certificate || {};

      const candidateList = [
        exam.certificateUrl,
        exam.certificate_url,
        apply.certificateUrl,
        apply.certificate_url,
        certificate.certificateUrl,
        certificate.certificate_url
      ];
      for (let i = 0; i < candidateList.length; i += 1) {
        const normalized = this.normalizeCertificateUrl(candidateList[i]);
        if (normalized) {
          return normalized;
        }
      }
      return "";
    },
    async fetchCertificateUrlByApplyApi() {
      const ftId = this.resolvePaperUploadFtId();
      if (!ftId) {
        return "";
      }
      try {
        const res = await httpRequest("/app/exam/apply", "get", { ftId });
        if (!(res && res.code === 200 && res.data)) {
          return "";
        }
        return this.normalizeCertificateUrl(
          res.data.certificateUrl || res.data.certificate_url
        );
      } catch (error) {
        void error;
        return "";
      }
    },
    async openCertificatePreview() {
      if (this.certificateLoading) {
        return;
      }
      this.certificateLoading = true;
      try {
        let certificateUrl = this.resolveCurrentCertificateUrlFromExam();
        if (!certificateUrl) {
          certificateUrl = await this.fetchCertificateUrlByApplyApi();
        }
        if (!certificateUrl) {
          this.$message.warning("暂无证书");
          return;
        }
        this.certPreviewUrl = certificateUrl;
        this.showCertOverlay = true;
      } finally {
        this.certificateLoading = false;
      }
    },
    async downloadCertificate() {
      if (!this.certPreviewUrl || this.downloadingCert) {
        return;
      }
      this.downloadingCert = true;
      try {
        const response = await fetch(this.certPreviewUrl);
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const blob = await response.blob();

        // Derive a default filename with person name + training name
        const extMatch = this.certPreviewUrl.match(/\.(jpg|png|jpeg|pdf)(\?|$)/i);
        const ext = extMatch ? extMatch[1].toLowerCase() : "png";
        const userName = (this.userInfo && this.userInfo.realName) || "";
        const examName = (this.currentExam && this.currentExam.name) || "";
        const defaultName = "培训合格证书" + (userName ? "_" + userName : "") + (examName ? "_" + examName : "") + "." + ext;

        // Try the File System Access API (shows native "Save As" dialog)
        if (window.showSaveFilePicker) {
          try {
            const mimeMap = { png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", pdf: "application/pdf" };
            const handle = await window.showSaveFilePicker({
              suggestedName: defaultName,
              types: [{
                description: "证书文件",
                accept: { [mimeMap[ext] || "image/png"]: ["." + ext] }
              }]
            });
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();
            this.$message.success("证书已保存");
            return;
          } catch (pickerErr) {
            // User cancelled the picker – not an error
            if (pickerErr.name === "AbortError") {
              return;
            }
            // Fallback to legacy download if picker fails for other reasons
          }
        }

        // Fallback: legacy <a download> approach
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = defaultName;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      } catch (error) {
        console.error("Download failed:", error);
        this.$message.error("下载证书失败，请稍后重试");
      } finally {
        this.downloadingCert = false;
      }
    },
    resolvePaperUploadFtId() {
      const exam = this.currentExam;
      const formals = Array.isArray(exam && exam.formals) ? exam.formals : [];
      if (formals.length === 0) {
        return null;
      }
      const sorted = formals
        .filter(item => Number(item && item.ftId) > 0)
        .slice()
        .sort((a, b) => {
          const sortA = Number(a && a.sort);
          const sortB = Number(b && b.sort);
          if (sortA !== sortB) {
            return sortB - sortA;
          }
          return Number(b && b.ftId) - Number(a && a.ftId);
        });
      if (sorted.length === 0) {
        return null;
      }
      return Number(sorted[0].ftId);
    },
    openPaperUploadDialog() {
      if (!this.showPaperUploadEntry) {
        return;
      }
      if (this.paperUploadState.buttonDisabled || this.uploadingPaper) {
        return;
      }
      const apply = this.currentExam && this.currentExam.apply;
      this.paperUploadFileName = (apply && apply.paperName) || "";
      this.paperUploadDialogVisible = true;
    },
    async handlePaperUploadFile(params) {
      if (!params || !params.file) {
        return;
      }
      const ftId = this.resolvePaperUploadFtId();
      if (!ftId) {
        this.$message.warning("考试记录异常，无法上传论文");
        if (params.onError) {
          params.onError(new Error("NO_FORMAL_TEST_ID"));
        }
        return;
      }
      if (this.uploadingPaper) {
        return;
      }
      this.uploadingPaper = true;
      this.paperUploadFileName = params.file.name;
      const formData = new FormData();
      formData.append("officeFile", params.file);
      try {
        const fileRes = await httpRequest(
          params.action || "/file/importOffice",
          "post",
          formData
        );
        if (
          !(
            fileRes &&
            fileRes.code === 200 &&
            Array.isArray(fileRes.data) &&
            fileRes.data[0]
          )
        ) {
          this.$message.error("论文文件上传失败，请重试");
          if (params.onError) {
            params.onError(new Error("UPLOAD_FILE_FAILED"));
          }
          return;
        }
        const saveRes = await httpRequest(
          "/app/exam/apply/upload-paper",
          "put",
          {
            paperUrl: fileRes.data[0],
            paperName: params.file.name,
            ftId
          }
        );
        if (!(saveRes && saveRes.code === 200)) {
          if (params.onError) {
            params.onError(new Error("SAVE_PAPER_FAILED"));
          }
          return;
        }
        if (this.currentExam && this.currentExam.apply) {
          this.currentExam.apply.paperUrl = fileRes.data[0];
          this.currentExam.apply.paperName =
            (saveRes.data && saveRes.data.fileName) || params.file.name;
          this.currentExam.apply.isCheckPass = 0;
        }
        this.paperUploadDialogVisible = false;
        this.$message.success("论文上传成功，请等待审核");
        await this.loadExamLibData();
        if (params.onSuccess) {
          params.onSuccess(saveRes, params.file);
        }
      } catch (error) {
        void error;
        this.$message.error("论文上传失败，请稍后重试");
        if (params.onError) {
          params.onError(error);
        }
      } finally {
        this.uploadingPaper = false;
      }
    },
    isSurveySubmittedLocal(arId) {
      const currentArId = Number(arId);
      if (!Number.isFinite(currentArId) || currentArId <= 0) {
        return false;
      }
      try {
        const raw = sessionStorage.getItem(SUBMITTED_THEORY_SURVEY_CACHE_KEY);
        const list = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(list)) {
          return false;
        }
        return list.includes(currentArId);
      } catch (error) {
        void error;
        return false;
      }
    },
    isBaseFeedbackSubmittedLocal(arId) {
      const currentArId = Number(arId);
      if (!Number.isFinite(currentArId) || currentArId <= 0) {
        return false;
      }
      try {
        const raw = sessionStorage.getItem(SUBMITTED_BASE_FEEDBACK_CACHE_KEY);
        const list = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(list)) {
          return false;
        }
        return list.includes(currentArId);
      } catch (error) {
        void error;
        return false;
      }
    },
    goToTheorySurvey() {
      const arId = Number(
        this.currentExam &&
          this.currentExam.apply &&
          this.currentExam.apply.arId
      );
      if (!Number.isFinite(arId) || arId <= 0) {
        this.$message.warning("调查问卷信息异常");
        return;
      }
      this.$router.push({
        path: "/investigation",
        query: {
          arId: String(arId)
        }
      });
    },
    goToBaseFeedback() {
      const arId = Number(
        this.currentExam &&
          this.currentExam.apply &&
          this.currentExam.apply.arId
      );
      if (!Number.isFinite(arId) || arId <= 0) {
        this.$message.warning("报名信息异常");
        return;
      }
      this.$router.push({
        path: "/trainScore",
        query: {
          arId: String(arId)
        }
      });
    },
    isExamAttemptsExhausted(exam) {
      const examCount = Number(exam && exam.apply && exam.apply.examCount);
      return Number.isFinite(examCount) && examCount >= 2;
    },
    guardExamEndedByTimes(exam) {
      if (!this.isExamAttemptsExhausted(exam)) {
        return false;
      }
      this.$message.warning("考试已结束");
      return true;
    },
    goToCurrentExamDetail() {
      const erId = this.getCurrentErId();
      if (!erId) {
        this.$message.warning("暂无可查看的报名详情");
        return;
      }
      this.$router.push({
        path: "/registrationDetails",
        query: {
          erId,
          isXQ: 1
        }
      });
    },
    handleHeroAction() {
      if (!this.heroStatus.showAction || this.heroStatus.actionDisabled) {
        return;
      }
      const erId = this.getCurrentErId();
      switch (this.heroStatus.actionType) {
        case "HOME":
          this.$router.push("/");
          return;
        case "EXAM": {
          if (this.guardExamEndedByTimes(this.currentExam)) {
            return;
          }
          this.enterExamFlowWithOngoingCheck();
          return;
        }
        case "PRACTICE":
          if (!erId) {
            this.$message.warning("实践考核信息异常");
            return;
          }
          this.$router.push({
            path: "/expert",
            query: {
              erId
            }
          });
          return;
        case "CERT":
          this.openCertificatePreview();
          return;
        case "CHOOSE_BASE":
          {
            const arId = this.getCurrentArId();
            const majorId = this.getCurrentMajorId();
            if (!arId || !majorId) {
              this.$message.warning("实习单位信息异常");
              return;
            }
            this.$router.push({
              path: "/choicePracticeBase",
              query: {
                arId: String(arId),
                majorId: String(majorId)
              }
            });
          }
          return;
        case "UPLOAD_PAPER":
          this.goToCurrentExamDetail();
          return;
        default:
          return;
      }
    },
    getLibId(item) {
      if (!item) {
        return "";
      }
      return item.elId || item.libId || item.lib_id || item.id || "";
    },
    getExamLibName(item) {
      if (!item) {
        return "未命名试题集";
      }
      return item.name || item.libName || item.lib_name || "未命名试题集";
    },
    getCoverUrl(item) {
      if (!item) {
        return this.defaultCover;
      }
      return item.thumbUrl || item.thumb_url || this.defaultCover;
    },
    getAnsweredQuestions(item) {
      const count = Number(
        item && (item.answerQuestions || item.answer_questions || item.answered)
      );
      return Number.isFinite(count) && count > 0 ? count : 0;
    },
    getTotalQuestions(item) {
      const count = Number(
        item &&
          (item.totalQuestions ||
            item.total_questions ||
            item.questionNum ||
            item.question_num ||
            item.total)
      );
      return Number.isFinite(count) && count > 0 ? count : 0;
    },
    getAccuracyPercent(item) {
      const raw = Number(item && item.accuracy);
      if (!Number.isFinite(raw) || raw <= 0) {
        return 0;
      }
      if (raw <= 1) {
        return Math.min(100, Number((raw * 100).toFixed(2)));
      }
      return Math.min(100, Number(raw.toFixed(2)));
    },
    getAnsweredPercent(item) {
      const answered = this.getAnsweredQuestions(item);
      const total = this.getTotalQuestions(item);
      if (total <= 0) {
        return 0;
      }
      return Math.min(100, Number(((answered / total) * 100).toFixed(2)));
    },
    isLibLocked(item) {
      if (!(item && item.apply)) {
        return true;
      }
      const exam = this.getExamInfo(item);
      return this.isBeforeTheoryStudyStart(exam);
    },
    getExamInfo(item) {
      if (!item || !item.exam) {
        return null;
      }
      return item.exam;
    },
    formatDateTime(value) {
      const date = this.parseDateTime(value);
      if (!date) {
        return "-";
      }
      const year = date.getFullYear();
      const month = `${date.getMonth() + 1}`.padStart(2, "0");
      const day = `${date.getDate()}`.padStart(2, "0");
      const hour = `${date.getHours()}`.padStart(2, "0");
      const minute = `${date.getMinutes()}`.padStart(2, "0");
      const second = `${date.getSeconds()}`.padStart(2, "0");
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    },
    isBeforeTheoryStudyStart(exam) {
      const start = this.parseDateTime(exam && exam.examStartTime);
      if (!start) {
        return false;
      }
      return Date.now() < start.getTime();
    },
    getTheoryStudyLockedMessage(exam) {
      const start = this.formatDateTime(exam && exam.examStartTime);
      const end = this.formatDateTime(exam && exam.examEndTime);
      return `暂未开启，理论学习时间：${start}~${end}`;
    },
    isInTheoryStudyProcess(exam) {
      if (!exam) {
        return false;
      }
      const now = Date.now();
      const start = this.parseDateTime(exam.examStartTime);
      const end = this.parseDateTime(exam.examEndTime);
      if (start && now < start.getTime()) {
        return false;
      }
      if (end && now > end.getTime()) {
        return false;
      }
      return true;
    },
    isLearnHoursEnough(apply) {
      if (!apply) {
        return false;
      }
      const needDuration = Number(apply.needLearnDuration);
      if (!Number.isFinite(needDuration) || needDuration <= 0) {
        return true;
      }
      const doneDuration = Number(apply.yetLearnDuration);
      return Number.isFinite(doneDuration) && doneDuration >= needDuration;
    },
    shouldBlockFormalExamByLearnHours() {
      const exam = this.currentExam;
      if (!exam || !exam.apply) {
        return false;
      }
      if (!this.isInTheoryStudyProcess(exam)) {
        return false;
      }
      return !this.isLearnHoursEnough(exam.apply);
    },
    isFunctionTemporarilyLocked(item) {
      if (!item || !item.path) {
        return false;
      }
      const lockPaths = ["/practice", "/mockExamLib", "/formalExam"];
      if (!lockPaths.includes(item.path)) {
        return false;
      }
      if (!this.currentExam || !this.currentExam.apply) {
        return false;
      }
      return this.isBeforeTheoryStudyStart(this.currentExam);
    },
    parseDateTime(value) {
      if (!value) {
        return null;
      }
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
      }
      const normalized = String(value).replace(/-/g, "/");
      const parsed = new Date(normalized);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }
      const fallback = new Date(value);
      return Number.isNaN(fallback.getTime()) ? null : fallback;
    },
    isEnrollOngoing(item) {
      const exam = this.getExamInfo(item);
      if (!exam || item.apply) {
        return false;
      }
      const now = new Date();
      const start = this.parseDateTime(exam.enrollStartTime);
      const end = this.parseDateTime(exam.enrollEndTime);
      if (!end) {
        return false;
      }
      if (start && now < start) {
        return false;
      }
      return now <= end;
    },
    shouldShowEnrollTip(item) {
      return this.isEnrollOngoing(item);
    },
    getEnrollTipText(item) {
      const exam = this.getExamInfo(item);
      if (!exam || !exam.enrollEndTime) {
        return "";
      }
      return `${exam.enrollEndTime}报名结束`;
    },
    getPracticeRouteQuery() {
      const currentExam = this.currentExam || {};
      const currentMajorType = Number(currentExam.majorType);
      const majorType = Number.isFinite(currentMajorType)
        ? currentMajorType
        : this.majorType;
      const majorId = this.resolveMajorIdForAnswer(
        this.currentExam ? [this.currentExam] : this.yetEnrollExams
      );
      if (majorId === "" || majorId === null || majorId === undefined) {
        return { majorType };
      }
      return {
        majorType,
        majorId
      };
    },
    getMockExamRouteQuery() {
      const currentExam = this.currentExam || {};
      const currentMajorType = Number(currentExam.majorType);
      const majorType = Number.isFinite(currentMajorType)
        ? currentMajorType
        : this.majorType;
      const majorId = this.resolveMajorIdForAnswer(
        this.currentExam ? [this.currentExam] : this.yetEnrollExams
      );
      const erId = this.getCurrentErId();
      const query = { majorType };
      if (!(majorId === "" || majorId === null || majorId === undefined)) {
        query.majorId = majorId;
      }
      if (erId) {
        query.erId = erId;
      }
      return query;
    },
    formatDurationText(totalSeconds) {
      const seconds = Number(totalSeconds);
      if (!Number.isFinite(seconds) || seconds <= 0) {
        return "";
      }
      const safeSeconds = Math.floor(seconds);
      const hours = Math.floor(safeSeconds / 3600);
      const minutes = Math.floor((safeSeconds % 3600) / 60);
      const remainSeconds = safeSeconds % 60;
      if (hours > 0) {
        return `${hours}小时${minutes}分`;
      }
      return `${minutes}分${String(remainSeconds).padStart(2, "0")}秒`;
    },
    async enterExamFlowWithOngoingCheck() {
      const erId = this.getCurrentErId();
      if (!erId) {
        this.$message.warning("考试信息异常");
        return;
      }
      if (this.guardExamEndedByTimes(this.currentExam)) {
        return;
      }
      let ongoingData = null;
      try {
        const ongoingRes = await httpRequest(
          "/app/formal-test/ongoing",
          "get",
          {
            erId
          }
        );
        if (ongoingRes && ongoingRes.code === 200 && ongoingRes.data) {
          ongoingData = ongoingRes.data;
        }
      } catch (error) {
        void error;
      }
      if (ongoingData && ongoingData.hasOngoing) {
        const remainText = this.formatDurationText(ongoingData.leftSeconds);
        const remainTips = remainText ? `（剩余时间约${remainText}）` : "";
        const canRestart = ongoingData.canRestart !== false;
        if (!canRestart) {
          await this.$alert(
            `检测到您有一场正在进行中的考试${remainTips}，当前为最后一次考试机会，请继续完成本场考试。`,
            "提示",
            {
              confirmButtonText: "继续考试",
              closeOnClickModal: false,
              closeOnPressEscape: false,
              showClose: false,
              type: "warning"
            }
          );
          this.$router.push({
            path: "/formalExam",
            query: {
              erId: String(erId)
            }
          });
          return;
        }
        try {
          await this.$confirm(
            `检测到您有一场正在进行中的考试${remainTips}，如果选择重新考试将提交当前答卷。您还剩一次考试机会，请谨慎考虑。`,
            "提示",
            {
              confirmButtonText: "继续考试",
              cancelButtonText: "重新考试",
              distinguishCancelAndClose: true,
              closeOnClickModal: false,
              closeOnPressEscape: false,
              type: "warning"
            }
          );
          this.$router.push({
            path: "/formalExam",
            query: {
              erId: String(erId)
            }
          });
          return;
        } catch (action) {
          if (action !== "cancel") {
            return;
          }
          let submitRes = null;
          try {
            submitRes = await httpRequest(
              `/app/formal-test/submit-ongoing-and-restart/${erId}`,
              "put",
              null
            );
          } catch (error) {
            void error;
          }
          if (!(submitRes && submitRes.code === 200)) {
            this.$message.error(
              (submitRes && submitRes.message) || "提交进行中考试失败，请重试"
            );
            return;
          }
          this.$message.success("已提交当前考试，可重新开始考试");
          this.$router.push({
            path: "/examInfo",
            query: {
              erId: String(erId)
            }
          });
          return;
        }
      }
      this.$router.push({
        path: "/examInfo",
        query: {
          erId: String(erId)
        }
      });
    },
    handleFunctionClick(item) {
      if (item && item.path) {
        if (
          item.path === "/formalExam" &&
          this.guardExamEndedByTimes(this.currentExam)
        ) {
          return;
        }
        if (this.isFunctionTemporarilyLocked(item)) {
          this.$message.warning(
            this.getTheoryStudyLockedMessage(this.currentExam)
          );
          return;
        }
        if (
          item.path === "/formalExam" &&
          this.shouldBlockFormalExamByLearnHours()
        ) {
          this.$alert("学时未满，没有开放考试", "提示", {
            confirmButtonText: "我知道了"
          });
          return;
        }
        if (item.path === "/formalExam") {
          this.enterExamFlowWithOngoingCheck();
          return;
        }
        if (item.path === "/practice") {
          this.$router.push({
            path: "/practice",
            query: this.getPracticeRouteQuery()
          });
          return;
        }
        if (item.path === "/mockExamLib") {
          this.$router.push({
            path: "/mockExamLib",
            query: this.getMockExamRouteQuery()
          });
          return;
        }
        if (item.query) {
          if (item.path === "/courseList") {
            const query = { ...item.query };
            const currentMajorType = Number(
              this.currentExam && this.currentExam.majorType
            );
            if (Number.isFinite(currentMajorType)) {
              query.majorType = currentMajorType;
            }
            this.$router.push({
              path: item.path,
              query
            });
            return;
          }
          this.$router.push({
            path: item.path,
            query: item.query
          });
          return;
        }
        this.$router.push(item.path);
      }
    },
    goToQuestionSet(item) {
      if (!(item && item.apply)) {
        this.$message.warning("请先报名对应培训班");
        return;
      }
      const exam = this.getExamInfo(item);
      if (this.isBeforeTheoryStudyStart(exam)) {
        this.$message.warning(this.getTheoryStudyLockedMessage(exam));
        return;
      }

      const libId = this.getLibId(item);
      if (!libId) {
        this.$message.warning("题库信息异常");
        return;
      }
      this.currentExamLib = item;
      this.practiceType = 0;
      this.practiceDialogVisible = true;
    },
    getNoQuestionMessage() {
      if (this.practiceType === 1) {
        return "暂无未做的题目";
      }
      if (this.practiceType === 2) {
        return "暂无错题";
      }
      if (this.practiceType === 3) {
        return "暂无已答题目";
      }
      return "暂无题目";
    },
    checkPracticeTypeAvailable(totalInfo) {
      const totalQuestionCount =
        Number(totalInfo && totalInfo.totalQuestionCount) || 0;
      const notAnswerCount = Number(totalInfo && totalInfo.notAnswerCount) || 0;
      const wrongCount = Number(totalInfo && totalInfo.wrongCount) || 0;
      const answerCount = Number(totalInfo && totalInfo.answerCount) || 0;
      if (this.practiceType === 0) {
        return totalQuestionCount > 0;
      }
      if (this.practiceType === 1) {
        return notAnswerCount > 0;
      }
      if (this.practiceType === 2) {
        return wrongCount > 0;
      }
      if (this.practiceType === 3) {
        return answerCount > 0;
      }
      return false;
    },
    async go2PracticeExam() {
      const libId = this.getLibId(this.currentExamLib);
      if (!libId) {
        this.$message.warning("题库信息异常");
        return;
      }
      const loadingInstance = this.$loading({ text: "加载中" });
      try {
        const res = await httpRequest(
          `/app/answer/exam-lib-total/${libId}`,
          "get",
          null
        );
        if (!(res && res.code === 200 && res.data)) {
          this.$message.warning("题目数据加载失败");
          return;
        }
        if (!this.checkPracticeTypeAvailable(res.data)) {
          this.$message.warning(this.getNoQuestionMessage());
          return;
        }
        this.practiceDialogVisible = false;
        this.$router.push({
          path: "/practiceLibExam",
          query: {
            libId,
            answerType: this.practiceType
          }
        });
      } finally {
        loadingInstance.close();
      }
    }
  }
};
</script>

<style scoped lang="scss">
.study-page {
  min-height: calc(100vh - 77px - 50px);
  padding: 16px 0 26px;
  background: #efeff1;

  .study-shell {
    width: 1200px;
    max-width: calc(100% - 40px);
    margin: 0 auto;
  }

  .study-switcher {
    margin-bottom: 24px;
  }

  .major-tabs-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0;
    margin-bottom: 24px;
  }

  .major-tab {
    padding: 0 24px;
    height: 38px;
    line-height: 36px;
    font-size: 15px;
    color: #333333;
    background: #ffffff;
    border: 1px solid #3f93dc;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-weight: 500;
    text-align: center;
    box-sizing: border-box;

    &:not(:first-child) {
      margin-left: -1px;
    }

    &:first-child {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }

    &:last-child {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }

    &:hover {
      background: #f0f7ff;
      z-index: 1;
    }

    &.is-active {
      background: #3f93dc;
      color: #ffffff;
      border-color: #3f93dc;
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(63, 147, 220, 0.3);
      position: relative;
      z-index: 2;
    }
  }

  .exam-tabs-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .exam-tab {
    padding: 0 20px;
    height: 34px;
    line-height: 34px;
    background: #ffffff;
    border-radius: 17px;
    font-size: 14px;
    color: #606266;
    cursor: pointer;
    transition: all 0.3s;
    border: 1px solid transparent;
    max-width: 320px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    &:hover {
      color: #3f93dc;
    }

    &.is-active {
      background: #3f93dc;
      color: #ffffff;
      box-shadow: 0 2px 8px rgba(63, 147, 220, 0.3);
    }
  }

  .hero-panel {
    display: grid;
    grid-template-columns: 1fr 1fr;
    height: 245px;
    background: #ffffff;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 6px rgba(17, 31, 57, 0.06);
  }

  .hero-banner {
    position: relative;
    height: 100%;
    box-sizing: border-box;
    padding: 14px 16px 16px;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;

    &::after {
      content: "";
      position: absolute;
      inset: 0;
      background: linear-gradient(
        100deg,
        rgba(244, 251, 255, 0.5) 0%,
        rgba(230, 248, 255, 0.66) 38%,
        rgba(15, 103, 181, 0.2) 100%
      );
    }
  }

  .hero-banner-content {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 4px 0;
  }

  /* --- Top: Header --- */
  .hero-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .hero-status-tag {
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(4px);
    border-radius: 6px;
    font-size: 18px;
    font-weight: 600;
    line-height: 1;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  }

  .hero-detail-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 28px;
    padding: 0 10px;
    border: 1px solid rgba(255, 255, 255, 0.6);
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.3);
    color: #0e4c74;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.6);
    }

    i {
      font-size: 12px;
    }
  }

  /* --- Middle: Info --- */
  .hero-info {
    margin: 16px 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .hero-exam-name {
    margin: 0;
    font-size: 24px;
    line-height: 1.4;
    color: #0c3e5f;
    font-weight: 700;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-shadow: 0 1px 0 rgba(255, 255, 255, 0.5);
  }

  .hero-date {
    margin-top: 8px;
    font-size: 14px;
    color: #2f6588;
    display: flex;
    align-items: center;
    opacity: 0.9;
  }

  .hero-extra-fail-tip {
    margin-top: 10px;
    font-size: 16px;
    line-height: 1.5;
    color: #e6a23c;
    font-weight: 500;
  }

  /* --- Bottom: Footer --- */
  .hero-footer {
    margin-top: 16px;
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .hero-progress-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .hero-progress-text {
    display: flex;
    align-items: baseline;
    font-size: 13px;
    color: #1e638d;

    .label {
      margin-right: 6px;
      opacity: 0.8;
    }
    .value {
      font-size: 18px;
      font-weight: 700;
      color: #0f7cc4;
      margin-right: 6px;
      font-family: DINAlternate-Bold, Roboto, sans-serif;
    }
    .sub-text {
      font-size: 12px;
      opacity: 0.7;
    }
  }

  .hero-progress-bar {
    width: 100%;
    height: 10px; // Slightly thicker
    border-radius: 5px;
    background: rgba(255, 255, 255, 0.6);
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
    overflow: hidden;
  }

  .hero-progress-inner {
    display: block;
    height: 100%;
    border-radius: 5px;
    background: linear-gradient(90deg, #6caff3 0%, #3f93dc 100%);
    box-shadow: 0 2px 4px rgba(63, 147, 220, 0.3);
    transition: width 0.6s ease;
  }

  .hero-main-btn {
    flex-shrink: 0;
    height: 38px;
    padding: 0 24px;
    border: 0;
    border-radius: 19px;
    background: #3f93dc;
    color: #ffffff;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(63, 147, 220, 0.3);
    transition: all 0.3s;
    letter-spacing: 0.5px;

    &:hover {
      background: #3681c2;
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(63, 147, 220, 0.4);
    }

    &.is-disabled {
      background: #cbd3da;
      color: #fff;
      box-shadow: none;
      cursor: not-allowed;
      transform: none;
    }
  }

  /* --- Empty State --- */
  .hero-empty {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    color: #407d9f;
  }

  .hero-empty-icon {
    font-size: 48px;
    margin-bottom: 12px;
    opacity: 0.3;
  }

  .hero-empty-text {
    text-align: center;
  }

  .hero-empty-title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 6px;
  }

  .hero-empty-desc {
    font-size: 14px;
    opacity: 0.8;
  }

  .status-success {
    color: #0ba360;
    border-left: 3px solid #0ba360;
  }
  .status-warning {
    color: #e67e22;
    border-left: 3px solid #e67e22;
  }
  .status-error {
    color: #e74c3c;
    border-left: 3px solid #e74c3c;
  }
  .status-neutral {
    color: #3498db;
    border-left: 3px solid #3498db;
  }
  .status-muted {
    color: #7f8c8d;
    border-left: 3px solid #7f8c8d;
  }

  .hero-actions {
    padding: 10px 18px;
  }

  .function-grid {
    height: 100%;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px 10px;
    align-content: center;
  }

  .function-item {
    width: 100%;
    border: 0;
    background: transparent;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    cursor: pointer;
    padding: 10px 6px;
    transition: transform 0.2s ease, background-color 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      background: #f5f8fc;
    }
  }

  .function-icon {
    width: 54px;
    height: 54px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;

    i {
      font-size: 30px;
      color: inherit;
    }
  }

  .function-name {
    font-size: 14px;
    color: #2a2f38;
    line-height: 1.2;
    font-weight: 400;
    letter-spacing: 0.2px;
    white-space: nowrap;
  }

  .study-hours-card {
    margin-top: 20px;
    background: #ffffff;
    border-radius: 14px;
    min-height: 84px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 34px;
    box-shadow: 0 1px 6px rgba(17, 31, 57, 0.06);
  }

  .survey-entry-card {
    margin-top: 16px;
    min-height: 76px;
    padding: 0 22px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 14px;
    background: #ffffff;
    box-shadow: 0 1px 6px rgba(17, 31, 57, 0.06);
  }

  .survey-entry-left {
    display: inline-flex;
    align-items: center;
    gap: 12px;
  }

  .survey-entry-icon {
    width: 32px;
    height: 32px;
    object-fit: contain;
  }

  .survey-entry-title {
    font-size: 18px;
    color: #149ca8;
    line-height: 1;
    font-weight: 600;
  }

  .survey-entry-btn {
    height: 38px;
    min-width: 100px;
    padding: 0 20px;
    border: 0;
    border-radius: 19px;
    color: #ffffff;
    font-size: 15px;
    font-weight: 500;
    background: #0da6a8;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.92;
    }
  }

  .paper-upload-entry-card {
    margin-top: 16px;
    min-height: 76px;
    padding: 0 22px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 14px;
    background: #ffffff;
    box-shadow: 0 1px 6px rgba(17, 31, 57, 0.06);
  }

  .paper-upload-entry-left {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .paper-upload-entry-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #19b2b8;
    font-size: 28px;
    flex-shrink: 0;
  }

  .paper-upload-entry-title {
    font-size: 18px;
    color: #149ca8;
    line-height: 1.3;
    font-weight: 600;

    &.is-pending {
      color: #e6a23c;
    }
    &.is-reviewing {
      color: #4095e5;
    }
    &.is-rejected {
      color: #f56c6c;
    }
    &.is-passed {
      color: #67c23a;
    }
  }

  .paper-upload-entry-btn {
    height: 38px;
    min-width: 120px;
    padding: 0 26px;
    border: 0;
    border-radius: 19px;
    color: #ffffff;
    font-size: 15px;
    font-weight: 500;
    background: #0da6a8;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.92;
    }

    &.is-disabled {
      background: #ccd5db;
      color: #ffffff;
      cursor: not-allowed;
      opacity: 1;
    }
  }

  .study-hours-label {
    font-size: 16px;
    color: #21252d;
    line-height: 1;
  }

  .study-hours-value {
    display: inline-flex;
    align-items: baseline;
    gap: 8px;
  }

  .study-hours-num {
    font-size: 42px;
    line-height: 1;
    color: #1cae74;
  }

  .study-hours-unit {
    font-size: 16px;
    color: #21252d;
    line-height: 1;
  }

  .exam-lib-section {
    margin-top: 20px;
    border-radius: 14px;
    background: #ffffff;
    overflow: hidden;
    box-shadow: 0 1px 6px rgba(17, 31, 57, 0.06);
  }

  .exam-lib-header {
    height: 62px;
    background: #3f93dc;
    color: #ffffff;
    text-align: center;
    font-size: 24px;
    line-height: 62px;
  }

  .exam-lib-body {
    padding: 20px;
  }

  .exam-lib-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 20px 16px;
  }

  .exam-lib-card {
    position: relative;
    display: flex;
    flex-direction: column;
    border-radius: 14px;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    background: #eef2f7;
    border: 1px solid #d9e2ea;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 18px rgba(44, 71, 109, 0.16);
    }
  }

  .exam-lib-cover {
    flex: 0 0 auto;
    height: 290px;
    background: #f6f8fa;

    .cover-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  .exam-lib-footer {
    flex: 0 0 auto;
    height: 44px;
    background: #c4ded8;
    display: flex;
    align-items: flex-start;
    padding: 8px 12px 6px 14px;
  }

  .footer-texts {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    row-gap: 4px;
    width: 100%;
  }

  .footer-top-line {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    column-gap: 8px;
  }

  .answered-text {
    font-size: 14px;
    color: #2a313c;
    line-height: 1;
  }

  .enroll-tip {
    width: 100%;
    font-size: 12px;
    color: #1ab8c5;
    line-height: 1.35;
    white-space: normal;
    word-break: break-all;
  }

  .enroll-tip-placeholder {
    visibility: hidden;
  }

  .lock-icon {
    font-size: 20px;
    color: #25b8c6;
    flex-shrink: 0;
  }

  ::v-deep .practice-dialog {
    width: 800px;
    border-radius: 10px;

    .lib-name {
      font-size: 20px;
      font-family: PingFangSC-Semibold, PingFang SC;
      font-weight: 600;
      color: #484848;
    }

    .lib-question-count {
      height: 21px;
      font-size: 15px;
      font-family: PingFangSC-Semibold, PingFang SC;
      font-weight: 600;
      color: #484848;
      line-height: 21px;
    }

    .lib-echarts {
      margin-top: 21px;

      .el-progress__text {
        font-size: 20px !important;
        font-family: Arial-Black, Arial !important;
        font-weight: 900 !important;
        color: #484848 !important;
      }

      svg path:first-child {
        stroke: #dcdfe6 !important;
      }

      .lib-answered-echart,
      .lib-accuracy-echart {
        width: 50%;
        height: 126px;
        text-align: center;
        display: inline-block;

        .lib-echart-title {
          height: 28px;
          font-size: 20px;
          font-family: PingFangSC-Regular, PingFang SC;
          font-weight: 400;
          color: #484848;
          line-height: 28px;
          margin-top: 12px;
        }
      }
    }

    .practice-type-list {
      width: 385px;
      height: 86px;
      background: #fbf9f9;
      border-radius: 10px;
      margin-left: auto;
      margin-right: auto;
      margin-top: 22px;

      div {
        display: inline-block;
        width: calc(50% - 81px);
        height: 43px;
        line-height: 43px;
        padding-left: 81px;
        font-size: 18px;
        font-family: PingFangSC-Semibold, PingFang SC;
        font-weight: 600;
        color: #484848;
        background-image: url("../../assets/practice/icon_radio_unselected.png");
        background-position: left 53px center;
        background-size: 20px 20px;
        background-repeat: no-repeat;
        cursor: pointer;
        text-align: left;
      }

      .selected-practice-type {
        background-image: url("../../assets/practice/icon_radio_selected.png");
      }
    }

    .practice-btn {
      height: 44px;
      line-height: 44px;
      width: 336px;
      margin-left: auto;
      margin-right: auto;
      margin-top: 30px;
      background: #00b0ab;
      border-radius: 4px;
      font-size: 17px;
      font-family: PingFangSC-Medium, PingFang SC;
      font-weight: 500;
      color: #ffffff;
      cursor: pointer;
      text-align: center;
    }
  }

  ::v-deep .paper-upload-dialog {
    width: 860px;
    max-width: calc(100% - 40px);
    border-radius: 4px;

    .el-dialog__header {
      padding: 22px 24px 8px;
      text-align: center;
    }

    .el-dialog__title {
      font-size: 22px;
      line-height: 1;
      color: #30343b;
      font-weight: 400;
    }

    .el-dialog__headerbtn {
      top: 16px;
      right: 18px;

      .el-dialog__close {
        font-size: 24px;
        color: #8f959d;
      }
    }

    .el-dialog__body {
      padding: 16px 40px 30px;
    }
  }

  .paper-upload-dialog-body {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .paper-file-uploader {
    margin-top: 6px;
  }

  .paper-select-btn {
    width: 180px;
    height: 44px;
    border: none;
    border-radius: 22px;
    background: #4095e5;
    color: #ffffff;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s;
    box-shadow: 0 4px 10px rgba(64, 149, 229, 0.3);
    letter-spacing: 1px;

    &:hover:not(:disabled) {
      background: #53a8ff;
      box-shadow: 0 6px 14px rgba(64, 149, 229, 0.4);
      transform: translateY(-1px);
    }

    &:active:not(:disabled) {
      transform: translateY(1px);
      box-shadow: 0 2px 6px rgba(64, 149, 229, 0.3);
    }

    &:disabled {
      background: #ccd5db;
      box-shadow: none;
      cursor: not-allowed;
      transform: none;
    }
  }

  .paper-selected-file {
    margin-top: 16px;
    min-height: 24px;
    font-size: 14px;
    line-height: 1.4;
    color: #606972;
    text-align: center;
    word-break: break-all;
  }

  .paper-close-btn {
    margin-top: 30px;
    width: 200px;
    height: 44px;
    border: 1px solid #dcdfe6;
    border-radius: 22px;
    color: #606266;
    background: #ffffff;
    font-size: 15px;
    cursor: pointer;
    transition: all 0.3s;
    letter-spacing: 1px;

    &:hover {
      color: #4095e5;
      border-color: #c6e2ff;
      background: #ecf5ff;
    }

    &:active {
      background: #e6f1fc;
    }
  }

  ::v-deep .cert-preview-dialog {
    width: 1100px;
    max-width: calc(100% - 40px);
    border-radius: 8px;

    .el-dialog__header {
      padding: 12px 16px 0;
    }

    .el-dialog__body {
      padding: 10px 18px 18px;
    }
  }

  .cert-preview-body {
    min-height: 240px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .cert-preview-img {
    display: block;
    max-height: calc(100vh - 240px);
    object-fit: contain;
    border-radius: 4px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    border: 1px solid #ebeef5;
  }
}

@media (max-width: 1280px) {
  .study-page {
    .switch-major-row .switch-tab {
      min-width: 72px;
      padding: 0 18px;
      font-size: 16px;
    }

    .switch-exam-tab {
      max-width: 260px;
    }

    .cert-preview-img {
      max-height: calc(100vh - 200px);
    }

    .hero-panel {
      grid-template-columns: 1fr;
      height: auto;
    }

    .hero-banner {
      min-height: 220px;
    }

    .function-grid {
      padding: 8px 0;
    }
  }
}

.cert-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #ebeef5;
  padding-bottom: 12px;
  
  .cert-dialog-title {
    font-size: 18px;
    font-weight: 600;
    color: #303133;
  }
  
  .cert-dialog-actions {
    display: flex;
    align-items: center;
    gap: 16px;
    
    .close-icon {
      font-size: 20px;
      color: #909399;
      cursor: pointer;
      transition: color 0.2s;
      
      &:hover {
        color: #4095e5;
      }
    }
  }
}
</style>
